import React, {createRef, useCallback, useEffect, useRef, useState} from "react";
import TextField from "@material-ui/core/TextField";
import {Button, DialogContent, DialogTitle, TextareaAutosize} from "@material-ui/core";
import z from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {
    useDeleteVehicleListContent,
    useGetVehicleContent,
    useGetVehicleListContent,
    usePatchVehicleContent,
    usePatchVehicleListContent,
    usePostVehicleListContent,
} from "../../../../Data/ApiHooks/UseContentHooks";
import useSnackbar from "../../../../CustomHooks/useSnackbar";
import PlaceholderImage from "../../../../Assets/placeholder-image.png";
import Dialog from "@material-ui/core/Dialog";
import PropTypes from "prop-types";

// region Schema
const formSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().min(1, "Description is required"),
});
// endregion

const VehicleEditorTab = ({ setShowEditedAlert }) => {
    // region Refs
    const driverTypeInputRefs = useRef([]);
    // endregion

    // region API Hooks
    const { data } = useGetVehicleContent();
    const { data: vehicleListData, refetch: refetchVehicleListData } = useGetVehicleListContent();
    const { mutate, isLoading } = usePatchVehicleContent({ onSuccess: () => onUpdateVehicleContentSuccess(), onError: (error) => onUpdateVehicleContentError(error) });
    const { mutate: postVehicleListContent } = usePostVehicleListContent({ onSuccess: () => onUpdateVehicleContentSuccess(), onError: (error) => onUpdateVehicleContentError(error) });
    const { mutate: patchVehicleListContent } = usePatchVehicleListContent({ onSuccess: () => onUpdateVehicleContentSuccess(), onError: (error) => onUpdateVehicleContentError(error) });
    const { mutate: deleteVehicleListContent } = useDeleteVehicleListContent({ onSuccess: () => onUpdateVehicleContentSuccess(), onError: (error) => onUpdateVehicleContentError(error) });
    // endregion

    // region State

    const [vehicleList, setVehicleList] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [vehicleIndexToDelete, setVehicleIndexToDelete] = useState(null);

    // endregion

    // region Custom hooks

    const { showSnackBar } = useSnackbar();

    // endregion

    // region Query callback functions

    function onUpdateVehicleContentSuccess() {
        showSnackBar("Vehicle content updated successfully", "success");
        refetchVehicleListData().then();
    }

    function onUpdateVehicleContentError(error) {
        showSnackBar(error, "error");
    }

    // endregion

    // region Form Hooks
    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
    } = useForm({
        resolver: zodResolver(formSchema),
    });
    // endregion

    // region Functions

    const createImageUrl = useCallback((imageUrl) => {
        if (!imageUrl) {
            return PlaceholderImage;
        }

        return imageUrl;
    }, [])

    // endregion

    // region Handlers
    const handleAddDriverType = useCallback((e, index) => {
        if (e.key === "Enter" && e.target.value.trim()) {
            const updatedVehicleList = [...vehicleList];
            updatedVehicleList[index] = {
                ...updatedVehicleList[index],
                properties: [...updatedVehicleList[index].properties, e.target.value.trim()]
            };
            setVehicleList(updatedVehicleList);
            e.target.value = "";
        }
    }, [vehicleList]);

    const handleDeleteDriverType = useCallback((propertyIndex, vehicleIndex) => {
        const updatedVehicleList = [...vehicleList];
        updatedVehicleList[vehicleIndex] = {
            ...updatedVehicleList[vehicleIndex],
            properties: updatedVehicleList[vehicleIndex].properties.filter((_, i) => i !== propertyIndex)
        };
        setVehicleList(updatedVehicleList);
    }, [vehicleList]);

    const handleAddVehicle = useCallback(() => {
        const newVehicle = {
            id: `new-${Date.now()}`,
            title: "",
            description: "",
            properties: [],
            image: PlaceholderImage
        };
        setVehicleList([newVehicle, ...vehicleList]);
    }, [vehicleList]);

    const handleVehicleTitleChange = useCallback((e, index) => {
        const updatedVehicleList = [...vehicleList];
        updatedVehicleList[index] = {
            ...updatedVehicleList[index],
            title: e.target.value
        };
        setVehicleList(updatedVehicleList);
    }, [vehicleList]);

    const handleVehicleImageChange = useCallback((e, index) => {
        const file = e.target.files[0];
        if (file) {
            const updatedVehicleList = [...vehicleList];
            updatedVehicleList[index] = {
                ...updatedVehicleList[index],
                image: URL.createObjectURL(file),
                imageFile: file
            };
            setVehicleList(updatedVehicleList);
        }
    }, [vehicleList]);

    const handleVehicleListSubmit = useCallback(() => {
        vehicleList.forEach((vehicle) => {
            const formData = new FormData();
            formData.append("title", vehicle.title);
            formData.append("properties", JSON.stringify(vehicle.properties));
            if (vehicle.imageFile) {
                formData.append("image", vehicle.imageFile);
            }

            if (vehicle.id.includes("new-")) {
                postVehicleListContent(formData);
            } else {
                formData.append("id", vehicle.id);
                patchVehicleListContent(formData);
            }
        });
    }, [vehicleList, postVehicleListContent, patchVehicleListContent]);

    const handleOnSubmit = useCallback((formData) => {
        if (!data) {
            return;
        }

        const jsonData = {
            id: data.id,
            vehicle_service_title: formData.title || "",
            vehicle_description: formData.description || ""
        };

        mutate(jsonData);
        handleVehicleListSubmit();
    }, [data, mutate, handleVehicleListSubmit]);

    const handleVehicleDeletion = useCallback((vehicleIndex) => {
        const updatedVehicleList = [...vehicleList];
        updatedVehicleList.splice(vehicleIndex, 1);
        setVehicleList(updatedVehicleList);

        const vehicleId = vehicleList[vehicleIndex].id;

        if (!vehicleId.includes("new-")) {
            deleteVehicleListContent(vehicleId);
        }

        setOpenModal(false);
    }, [deleteVehicleListContent, vehicleList]);
    // endregion

    // region Effects

    useEffect(() => {
        if (isDirty) {
            setShowEditedAlert(isDirty);
        }
    }, [isDirty, setShowEditedAlert]);

    useEffect(() => {
        if (data) {
            reset({
                title: data.vehicle_service_title || "",
                description: data.vehicle_description || ""
            });
        }
    }, [data, reset]);

    useEffect(() => {
        if (vehicleListData) {
            setVehicleList(vehicleListData || []);
            driverTypeInputRefs.current = Array(vehicleListData?.length || 0)
                .fill()
                .map((_, i) => driverTypeInputRefs.current[i] || createRef());
        }
    }, [vehicleListData]);

    useEffect(() => {
        driverTypeInputRefs.current = Array(vehicleList.length)
            .fill()
            .map((_, i) => driverTypeInputRefs.current[i] || createRef());
    }, [vehicleList.length]);
    // endregion

    // region Render
    return (
        <>
            <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                <DialogTitle className="dialog-title">Do you want to delete this vehicle?</DialogTitle>
                <DialogContent className="dialog-content-container">
                    <div className="delete-button-container">
                        <Button className="delete-button" onClick={() => handleVehicleDeletion(vehicleIndexToDelete)}>
                            Yes
                        </Button>
                    </div>
                    <Button className="vehicle-delete-button-no" onClick={() => setOpenModal(false)}>No</Button>
                </DialogContent>
            </Dialog>
            <form className="driver-editor-form" onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(handleOnSubmit)(e);
            }}>
                <div className="button-container">
                    <Button
                        type="submit"
                        className="submit-button"
                        disabled={isLoading}
                    >
                        Update
                    </Button>
                </div>
                <div className="form-container">
                    <div className="form-field">
                        <label>Vehicle service title</label>
                        <TextField
                            {...register("title")}
                            className="text-input"
                            error={!!errors.title}
                            helperText={errors.title?.message}
                            fullWidth
                            placeholder="Enter title"
                        />
                    </div>

                    <div className="form-field">
                        <label>Vehicle description</label>
                        <TextareaAutosize
                            {...register("description")}
                            className="textarea"
                            error={!!errors.description}
                            placeholder="Enter description"
                            minRows={4}
                        />
                    </div>
                </div>

                <div className="separator"/>

                <div className="button-container">
                    <Button
                        type="button"
                        className="add-vehicle-button"
                        onClick={handleAddVehicle}
                    >
                        Add Vehicle
                    </Button>
                </div>

                <div className="vehicle-list-container">
                    {vehicleList.length === 0 ? "No vehicle available!" : vehicleList.map((vehicle, index) => {
                        return (
                            <div className="vehicle-container" key={vehicle.id}>
                                <div className="home-editor-form-input-container">
                                    <span className="field-title">Vehicle title</span>
                                    <TextField
                                        className="home-editor-form-input"
                                        helperText={errors.vehicleServiceTitle?.message}
                                        error={!!errors.vehicleServiceTitle}
                                        fullWidth
                                        placeholder="Enter vehicle service title"
                                        value={vehicle.title}
                                        onChange={(e) => handleVehicleTitleChange(e, index)}
                                    />

                                    <div className="form-field">
                                        <label>Vehicle properties</label>
                                        <div className="text-input-container" style={{ display: "flex", gap: "8px" }}>
                                            <TextField
                                                className="text-input"
                                                fullWidth
                                                inputRef={(el) => (driverTypeInputRefs.current[index] = el)}
                                                placeholder="Press Enter to add driver type"
                                                onKeyDown={(e) => handleAddDriverType(e, index)}
                                            />
                                            <Button
                                                onClick={() => {
                                                    const inputRef = driverTypeInputRefs.current[index];
                                                    if (inputRef?.value?.trim()) {
                                                        const updatedVehicleList = [...vehicleList];
                                                        updatedVehicleList[index] = {
                                                            ...updatedVehicleList[index],
                                                            properties: [...updatedVehicleList[index].properties, inputRef.value.trim()]
                                                        };
                                                        setVehicleList(updatedVehicleList);
                                                        inputRef.value = "";
                                                    }
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                        <div className="driver-types-list">
                                            {vehicle.properties && vehicle.properties.map((type, propertyIndex) => (
                                                <div key={propertyIndex} className="driver-type-item">
                                                    <span>{type}</span>
                                                    <Button className="delete-button"
                                                        onClick={() => handleDeleteDriverType(propertyIndex, index)}>Delete
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    id={`partner-image-upload-${vehicle.id}`}
                                    onChange={(e) => handleVehicleImageChange(e, index)}
                                />
                                <label htmlFor={`partner-image-upload-${vehicle.id}`}>
                                    <img
                                        src={createImageUrl(vehicle.image)}
                                        alt={`Gallery image ${index + 1}`}
                                        style={{ width: "100%", height: "200px", objectFit: "cover", cursor: "pointer" }}
                                    />
                                </label>

                                <div className="delete-button-container">
                                    <Button className="delete-button" onClick={() => {
                                        setOpenModal(true);
                                        setVehicleIndexToDelete(index);
                                    }}>
                                        Delete vehicle
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </form>
        </>
    );
    // endregion
};

VehicleEditorTab.propTypes = {
    setShowEditedAlert: PropTypes.func
};

export default VehicleEditorTab;
